/* Thin fetch wrapper for the real backend — every content read and
 * every form submission goes through here, never a hardcoded frontend
 * value. Handles the common loading/error/empty shape every page and
 * form component needs (task requirement #13). */

const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL ?? '';

export class ApiRequestError extends Error {
  status: number;
  code: string;
  fieldErrors?: Array<{ field: string; message: string }>;

  constructor(status: number, code: string, message: string, fieldErrors?: Array<{ field: string; message: string }>) {
    super(message);
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

let accessToken: string | null = null;
export function setAccessToken(token: string | null) {
  accessToken = token;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (res.status === 204) return undefined as T;

  let body: any = null;
  try {
    body = await res.json();
  } catch {
    // non-JSON response (e.g. audio bytes from /api/voice/tts) — caller
    // handles that case itself via a raw fetch, not this helper.
  }

  if (!res.ok) {
    const err = body?.error ?? { code: 'unknown_error', message: `Request failed with status ${res.status}` };
    throw new ApiRequestError(res.status, err.code, err.message, err.field_errors);
  }

  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
